import { Controller } from '@hotwired/stimulus'

const DEBOUNCE_MS = 1200
const MIN_LENGTH = 30

// Live review rail inside the create-template modal (utility_template_review_enabled):
// sends the body to the utility_review endpoint while the operator writes, then
// renders the approval chance, highlights the risky excerpts over the textarea
// and offers the AI rewrites. Choosing a rewrite replaces the body (variables
// converted to Meta's positional {{n}} format) and fills the buttons repeater
// with the suggested quick replies.
export default class extends Controller {
  static targets = [
    'bodyText', 'backdrop', 'riskCount',
    'emptyState', 'loadingState', 'errorState',
    'scorePanel', 'scoreValue', 'scoreBar', 'reasoning',
    'optionsSection', 'optionsList',
    'stepStart', 'stepText', 'stepWhere',
    'startCards', 'libraryPanel', 'libraryCard', 'libraryTab', 'momentChip',
    'chosenModelSummary', 'chosenModelTitle', 'chosenModelBody',
    'footerStart', 'footerText', 'footerSubmit', 'continueButton',
    'stepBadge', 'stepLabel', 'libraryIdField', 'similarSection', 'similarList'
  ]
  // requireOption: non-plaza operators may only proceed with a sanctioned body —
  // a library model or one of the AI rewrites, unedited. Their own free text
  // never goes to Meta's review as written.
  static values = { url: String, requireOption: Boolean }

  nextStep(event) {
    event.preventDefault()
    if (this.requireOptionValue && !this.bodySanctioned()) return
    if (!this.stepTextValid()) return
    this.fromModel = false
    this.setLibraryId('')
    this.chosenModelSummaryTarget.classList.add('hidden')
    this.showStep('where')
  }

  prevStep(event) {
    event.preventDefault()
    this.showStep(this.fromModel ? 'start' : 'text')
  }

  backToStart(event) {
    event.preventDefault()
    this.showStep('start')
  }

  startFromScratch(event) {
    event.preventDefault()
    this.fromModel = false
    this.setLibraryId('')
    this.showStep('text')
  }

  setLibraryId(value) {
    if (this.hasLibraryIdFieldTarget) this.libraryIdFieldTarget.value = value
  }

  showLibrary(event) {
    event.preventDefault()
    if (this.hasStartCardsTarget) this.startCardsTarget.classList.add('hidden')
    this.libraryPanelTarget.classList.remove('hidden')
    this.filterLibrary()
  }

  hideLibrary(event) {
    event.preventDefault()
    this.libraryPanelTarget.classList.add('hidden')
    if (this.hasStartCardsTarget) this.startCardsTarget.classList.remove('hidden')
  }

  setLibraryContext(event) {
    this.libraryContext = event.currentTarget.dataset.libraryContext
    this.libraryTabTargets.forEach((tab) => {
      const active = tab.dataset.libraryContext === this.libraryContext
      tab.classList.toggle('text-yellow-600', active)
      tab.classList.toggle('border-yellow-500', active)
      tab.classList.toggle('text-gray-500', !active)
      tab.classList.toggle('border-transparent', !active)
    })
    this.syncMomentChips()
    this.filterLibrary()
  }

  // Each context tab shows only its own moments; a filter that no longer
  // applies falls back to "Todos os momentos".
  syncMomentChips() {
    const context = this.libraryContext || 'sdr'
    let selectionLost = false
    this.momentChipTargets.forEach((chip) => {
      const contexts = chip.dataset.libraryContexts
      const visible = !contexts || contexts.split(' ').includes(context)
      chip.classList.toggle('hidden', !visible)
      if (!visible && chip.dataset.libraryMoment === this.libraryMoment) selectionLost = true
    })
    if (selectionLost) this.selectMoment('')
  }

  setLibraryMoment(event) {
    this.selectMoment(event.currentTarget.dataset.libraryMoment)
  }

  selectMoment(moment) {
    this.libraryMoment = moment
    this.momentChipTargets.forEach((chip) => {
      const active = chip.dataset.libraryMoment === this.libraryMoment
      chip.classList.toggle('bg-gray-900', active)
      chip.classList.toggle('text-white', active)
      chip.classList.toggle('bg-white', !active)
      chip.classList.toggle('border', !active)
      chip.classList.toggle('border-gray-200', !active)
      chip.classList.toggle('text-gray-600', !active)
    })
    this.filterLibrary()
  }

  filterLibrary() {
    this.libraryCardTargets.forEach((card) => {
      const contextMatch = card.dataset.libraryContext === (this.libraryContext || 'sdr')
      const momentMatch = !this.libraryMoment || card.dataset.libraryMoment === this.libraryMoment
      card.classList.toggle('hidden', !(contextMatch && momentMatch))
    })
  }

  // A library model goes straight to "Onde usar": the body is pre-approved, so
  // there is nothing to review or edit — the text step is skipped entirely.
  useModel(event) {
    event.preventDefault()
    const { modelId, modelTitle, modelContext, modelBody, modelButtons } = event.currentTarget.dataset
    this.applyModel({
      id: modelId,
      title: modelTitle,
      context: modelContext,
      body: modelBody,
      buttons: JSON.parse(modelButtons || '[]')
    })
  }

  applyModel({ id, title, context, body, buttons }) {
    this.setLibraryId(id || '')

    const contextRadio = this.element.querySelector(`input[name="context_type"][value="${context}"]`)
    if (contextRadio) {
      contextRadio.checked = true
      contextRadio.dispatchEvent(new Event('change', { bubbles: true }))
    }

    const nameField = this.element.querySelector('[name="whatsapp_template[name]"]')
    if (nameField && !nameField.value) nameField.value = title

    this.selectedIndex = null
    this.modelBody = body
    this.fromModel = true
    this.setBody(body)
    this.buttonsController?.setQuickReplies(buttons || [])

    this.chosenModelTitleTarget.textContent = title
    this.chosenModelBodyTarget.textContent = body
    this.chosenModelSummaryTarget.classList.remove('hidden')
    this.showStep('where')
  }

  bodySanctioned() {
    if (this.selectedIndex !== null) return true
    return Boolean(this.modelBody) && this.bodyTextTarget.value === this.modelBody
  }

  updateContinueState() {
    if (!this.hasContinueButtonTarget) return
    const enabled = !this.requireOptionValue || this.bodySanctioned()
    this.continueButtonTarget.disabled = !enabled
    this.continueButtonTarget.classList.toggle('opacity-50', !enabled)
    this.continueButtonTarget.classList.toggle('cursor-not-allowed', !enabled)
    this.continueButtonTarget.classList.toggle('cursor-pointer', enabled)
    this.continueButtonTarget.title = enabled ? '' : 'Escolha uma das versões sugeridas para continuar'
  }

  stepTextValid() {
    const fields = this.stepTextTarget.querySelectorAll('input, textarea, select')
    for (const field of fields) {
      if (!field.reportValidity()) return false
    }
    return true
  }

  showStep(step) {
    const activeIndex = { start: 0, text: 1, where: 2 }[step]
    this.stepStartTarget.classList.toggle('hidden', step !== 'start')
    this.stepTextTarget.classList.toggle('hidden', step !== 'text')
    this.stepWhereTarget.classList.toggle('hidden', step !== 'where')

    const footers = [
      [this.footerStartTarget, step === 'start'],
      [this.footerTextTarget, step === 'text'],
      [this.footerSubmitTarget, step === 'where']
    ]
    footers.forEach(([footer, visible]) => {
      footer.classList.toggle('hidden', !visible)
      footer.classList.toggle('flex', visible)
    })

    this.stepBadgeTargets.forEach((badge) => {
      const active = badge.dataset.step === step
      badge.classList.toggle('bg-gray-900', active)
      badge.classList.toggle('text-white', active)
      badge.classList.toggle('bg-gray-200', !active)
      badge.classList.toggle('text-gray-500', !active)
    })
    this.stepLabelTargets.forEach((label) => {
      const active = label.dataset.step === step
      label.classList.toggle('text-gray-900', active)
      label.classList.toggle('text-gray-400', !active)
    })
  }

  connect() {
    this.review = null
    this.originalText = null
    this.selectedIndex = null
    this.modelBody = null
    this.fromModel = false
    this.programmatic = false
    this.libraryContext = 'sdr'
    this.libraryMoment = ''
    this.syncMomentChips()
    this.filterLibrary()
    this.updateContinueState()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.abortRequest()
  }

  bodyChanged() {
    this.clearHighlights()
    clearTimeout(this.timer)
    if (this.programmatic) {
      this.programmatic = false
      return
    }

    this.clearSelection()
    this.updateContinueState()
    const text = this.bodyTextTarget.value
    if (text.trim().length < MIN_LENGTH) {
      this.reset()
      return
    }
    this.timer = setTimeout(() => this.analyze(text), DEBOUNCE_MS)
  }

  regenerate(event) {
    event.preventDefault()
    const text = this.originalText || this.bodyTextTarget.value
    if (text.trim().length >= MIN_LENGTH) this.analyze(text)
  }

  syncBackdrop() {
    this.backdropTarget.scrollTop = this.bodyTextTarget.scrollTop
  }

  async analyze(text) {
    this.abortRequest()
    this.abortController = new AbortController()
    this.showState('loading')

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ body_text: text }),
        signal: this.abortController.signal
      })
      if (!response.ok) throw new Error(`utility_review ${response.status}`)

      this.review = await response.json()
      this.originalText = text
      this.selectedIndex = null
      this.render()
    } catch (error) {
      if (error.name === 'AbortError') return
      this.showState('error')
    }
  }

  render() {
    this.showState('result')
    this.renderScore(this.review.approval_chance)
    this.reasoningTarget.textContent = this.review.reasoning || ''
    this.renderHighlights(this.originalText, this.review.risk_excerpts || [])
    this.renderSimilarModels(this.review.similar_models || [])
    this.renderOptions(this.review.options || [])
  }

  renderSimilarModels(models) {
    if (!this.hasSimilarSectionTarget) return
    this.similarSectionTarget.classList.toggle('hidden', models.length === 0)
    this.similarSectionTarget.classList.toggle('flex', models.length > 0)
    this.similarListTarget.innerHTML = ''
    models.forEach((model) => {
      const card = document.createElement('div')
      card.className = 'rounded-lg bg-white border border-amber-200 p-3 flex flex-col gap-1.5'
      const title = document.createElement('span')
      title.className = 'text-xs font-semibold text-gray-900'
      title.textContent = model.moment_label ? `${model.title} · ${model.moment_label}` : model.title
      const reason = document.createElement('p')
      reason.className = 'text-[11px] text-amber-700 m-0'
      reason.textContent = model.reason || ''
      const body = document.createElement('p')
      body.className = 'text-xs text-gray-600 m-0 whitespace-pre-wrap'
      body.textContent = model.body
      const use = document.createElement('button')
      use.type = 'button'
      use.className = 'self-start text-xs font-bold text-yellow-600 hover:text-yellow-700 transition-colors'
      use.textContent = 'Usar este modelo'
      use.addEventListener('click', (event) => {
        event.preventDefault()
        this.applyModel({
          id: model.id,
          title: model.title,
          context: model.context_type,
          body: model.body,
          buttons: model.buttons || []
        })
      })
      card.append(title, reason, body, use)
      this.similarListTarget.appendChild(card)
    })
  }

  renderScore(chance) {
    const value = Math.max(0, Math.min(100, parseInt(chance, 10) || 0))
    const palette = value >= 75
      ? { text: '#067647', bar: '#17B26A' }
      : value >= 40
        ? { text: '#B54708', bar: '#F79009' }
        : { text: '#B42318', bar: '#F04438' }

    this.scoreValueTarget.textContent = value
    this.scoreValueTarget.style.color = palette.text
    this.scoreBarTarget.style.width = `${value}%`
    this.scoreBarTarget.style.background = palette.bar
  }

  renderHighlights(text, excerpts) {
    const ranges = []
    excerpts.filter(Boolean).forEach((excerpt) => {
      let haystack = text
      let needle = excerpt
      if (haystack.indexOf(needle) === -1) {
        haystack = text.toLowerCase()
        needle = excerpt.toLowerCase()
      }
      let index = haystack.indexOf(needle)
      while (index !== -1) {
        ranges.push([index, index + needle.length])
        index = haystack.indexOf(needle, index + needle.length)
      }
    })

    const merged = this.mergeRanges(ranges)
    if (merged.length === 0) {
      this.clearHighlights()
      return
    }

    let html = ''
    let cursor = 0
    merged.forEach(([start, end]) => {
      html += this.escape(text.slice(cursor, start))
      html += `<mark style="background:#FEE4E2;border-bottom:2px solid #F04438;color:transparent;padding:0">${this.escape(text.slice(start, end))}</mark>`
      cursor = end
    })
    html += this.escape(text.slice(cursor))

    this.backdropTarget.innerHTML = html
    this.syncBackdrop()
    this.riskCountTarget.textContent = merged.length === 1 ? '1 trecho de risco' : `${merged.length} trechos de risco`
    this.riskCountTarget.classList.remove('hidden')
  }

  renderOptions(options) {
    this.optionsListTarget.innerHTML = ''
    if (options.length === 0) {
      this.optionsSectionTarget.classList.add('hidden')
      this.optionsSectionTarget.classList.remove('flex')
      return
    }

    options.forEach((option, index) => {
      this.optionsListTarget.appendChild(this.buildOptionCard(option, index))
    })
    this.optionsSectionTarget.classList.remove('hidden')
    this.optionsSectionTarget.classList.add('flex')
  }

  buildOptionCard(option, index) {
    const card = document.createElement('button')
    card.type = 'button'
    card.dataset.optionIndex = index
    card.className = 'w-full text-left rounded-xl border border-gray-200 bg-white p-3 flex gap-2.5 transition-all hover:border-gray-300'
    card.addEventListener('click', () => this.toggleOption(index))

    const radio = document.createElement('span')
    radio.dataset.optionRadio = ''
    radio.className = 'flex-none w-4 h-4 mt-0.5 rounded-full border-2 border-gray-300 bg-white'
    card.appendChild(radio)

    const content = document.createElement('div')
    content.className = 'flex-1 min-w-0 flex flex-col gap-1.5'

    const header = document.createElement('div')
    header.className = 'flex items-center justify-between gap-2'
    const title = document.createElement('span')
    title.className = 'text-xs font-semibold text-gray-900'
    title.textContent = option.title || `Versão ${index + 1}`
    const chance = document.createElement('span')
    const value = parseInt(option.approval_chance, 10) || 0
    chance.className = 'text-xs font-semibold whitespace-nowrap'
    chance.style.color = value >= 75 ? '#067647' : value >= 40 ? '#B54708' : '#B42318'
    chance.textContent = `${value} de chance`
    header.appendChild(title)
    header.appendChild(chance)
    content.appendChild(header)

    const body = document.createElement('p')
    body.className = 'text-xs leading-relaxed text-gray-600 whitespace-pre-wrap'
    body.textContent = option.body
    content.appendChild(body)

    if ((option.buttons || []).length > 0) {
      const buttons = document.createElement('div')
      buttons.className = 'flex flex-wrap gap-1.5'
      option.buttons.forEach((text) => {
        const chip = document.createElement('span')
        chip.className = 'text-[11px] px-2 py-0.5 rounded-full border border-gray-200 text-gray-500'
        chip.textContent = text
        buttons.appendChild(chip)
      })
      content.appendChild(buttons)
    }

    card.appendChild(content)
    return card
  }

  toggleOption(index) {
    if (this.selectedIndex === index) {
      this.selectedIndex = null
      this.setBody(this.originalText)
      this.render()
      this.updateContinueState()
      return
    }

    const option = this.review.options[index]
    this.selectedIndex = index
    this.setBody(this.normalizeVariables(option.body))
    this.buttonsController?.setQuickReplies(option.buttons || [])
    this.renderScore(option.approval_chance)
    this.clearHighlights()
    this.updateSelectionStyles()
    this.updateContinueState()
  }

  updateSelectionStyles() {
    this.optionsListTarget.querySelectorAll('[data-option-index]').forEach((card) => {
      const selected = Number(card.dataset.optionIndex) === this.selectedIndex
      card.classList.toggle('border-gray-900', selected)
      card.classList.toggle('bg-yellow-50', selected)
      card.classList.toggle('border-gray-200', !selected)
      card.classList.toggle('bg-white', !selected)
      const radio = card.querySelector('[data-option-radio]')
      radio.classList.toggle('border-gray-900', selected)
      radio.classList.toggle('border-4', selected)
      radio.classList.toggle('border-2', !selected)
      radio.classList.toggle('border-gray-300', !selected)
    })
  }

  // Named variables go to Meta as-is (parameter_format NAMED), which requires
  // lowercase ascii names — normalize whatever the rewrite emitted.
  normalizeVariables(body) {
    return body.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, raw) => {
      const name = raw
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
      return `{{${name}}}`
    })
  }

  setBody(text) {
    this.programmatic = true
    this.bodyTextTarget.value = text
    this.bodyTextTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  clearSelection() {
    if (this.selectedIndex === null) return
    this.selectedIndex = null
    this.updateSelectionStyles()
    this.updateContinueState()
  }

  clearHighlights() {
    this.backdropTarget.innerHTML = ''
    this.riskCountTarget.classList.add('hidden')
  }

  showState(state) {
    this.emptyStateTarget.classList.toggle('hidden', state !== 'empty')
    this.loadingStateTarget.classList.toggle('hidden', state !== 'loading')
    this.errorStateTarget.classList.toggle('hidden', state !== 'error')
    this.scorePanelTarget.classList.toggle('hidden', state !== 'result')
    if (state !== 'result') {
      this.optionsSectionTarget.classList.add('hidden')
      this.optionsSectionTarget.classList.remove('flex')
      if (this.hasSimilarSectionTarget) {
        this.similarSectionTarget.classList.add('hidden')
        this.similarSectionTarget.classList.remove('flex')
      }
    }
  }

  reset() {
    this.abortRequest()
    this.review = null
    this.originalText = null
    this.selectedIndex = null
    this.clearHighlights()
    this.showState('empty')
    this.updateContinueState()
  }

  abortRequest() {
    this.abortController?.abort()
    this.abortController = null
  }

  mergeRanges(ranges) {
    const sorted = [...ranges].sort((a, b) => a[0] - b[0])
    const merged = []
    sorted.forEach(([start, end]) => {
      const last = merged[merged.length - 1]
      if (last && start <= last[1]) {
        last[1] = Math.max(last[1], end)
      } else {
        merged.push([start, end])
      }
    })
    return merged
  }

  escape(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  get buttonsController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'template-buttons')
  }
}
